/**
 * Locale-aware date formatting utilities
 * These functions use the browser's locale or accept a locale parameter
 * For Arabic locale (ar), dates are displayed in Islamic (Hijri) calendar
 */

import {
  PAST_24_HOURS, PAST_7_DAYS, PAST_30_DAYS, PAST_YEAR, YEAR_TO_DATE,
} from '@/lib/constants'
import {
  getDayBefore, getWeekBefore, get30DaysBefore, getOneYearBefore, getYearToDate,
} from './utils'

/**
 * Get the current locale from the document or default to 'en-US'
 */
export const getCurrentLocale = (): string => {
  if (typeof document !== 'undefined') {
    return document.documentElement.lang || 'en-US'
  }
  return 'en-US'
}

/**
 * Get calendar type based on locale
 * Arabic locales use Islamic calendar, others use Gregorian
 */
const getCalendar = (locale: string): 'islamic' | 'gregory' => (
  locale?.startsWith('ar') ? 'islamic' : 'gregory'
)

/**
 * Format date as medium date with short time
 * @example Feb 28, 2022, 9:59 PM (en-US - Gregorian)
 * @example ٢٨ رجب ١٤٤٣ هـ، ٩:٥٩ م (ar - Islamic/Hijri)
 */
export const formatMediumDateShortTime = (datetime: string | Date, locale?: string): string => {
  const date = new Date(datetime)
  const targetLocale = locale || getCurrentLocale()
  const calendar = getCalendar(targetLocale)

  return date.toLocaleString(targetLocale, {
    calendar,
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

/**
 * Format date as long format with time
 * @example March 19, 2025, 10:00 PM (en-US - Gregorian)
 * @example ١٦ شعبان ١٤٤٦ هـ ١٠:٠٠ م (ar - Islamic/Hijri)
 */
export const longFormatDate = (isoString: string | Date, locale?: string): string => {
  const date = new Date(isoString)
  const targetLocale = locale || getCurrentLocale()
  const calendar = getCalendar(targetLocale)

  return date.toLocaleString(targetLocale, {
    calendar,
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

/**
 * Format date as short date and time without year
 * @example Apr 25, 3:28 PM (en-US - Gregorian)
 * @example ٢٣ شوال، ٣:٢٨ م (ar - Islamic/Hijri)
 */
export const shortDateTimeNoYear = (datetime: string | Date, locale?: string): string => {
  const date = new Date(datetime)
  const targetLocale = locale || getCurrentLocale()
  const calendar = getCalendar(targetLocale)

  return date.toLocaleDateString(targetLocale, {
    calendar,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  })
}

/**
 * Format date as short date
 * @example Apr 26, 2022 (en-US - Gregorian)
 * @example ٢٤ رمضان ١٤٤٣ هـ (ar - Islamic/Hijri)
 */
export const formatShortDate = (datetime: string | Date, locale?: string): string => {
  const date = new Date(datetime)
  const targetLocale = locale || getCurrentLocale()
  const calendar = getCalendar(targetLocale)

  return date.toLocaleDateString(targetLocale, {
    calendar,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * Format time only
 * @example 2:25 PM (en-US)
 * @example 2:25:30 PM (en-US, showSeconds: true)
 * @example ٢:٢٥ م (ar)
 * Note: Time format is the same for both calendars
 */
export const formatShortTime = (
  datetime: string | Date,
  locale?: string,
  options?: { showSeconds?: boolean },
): string => {
  const date = new Date(datetime)
  const targetLocale = locale || getCurrentLocale()

  return date.toLocaleTimeString(targetLocale, {
    hour: 'numeric',
    minute: 'numeric',
    ...(options?.showSeconds && { second: 'numeric' }),
  })
}

/**
 * Format date as YYYY-MM-DD (always the same regardless of locale)
 * @example 2022-04-26
 */
export const formatYYYYMMDD = (dateStr: string | Date): string => {
  const date = new Date(dateStr)
  return date.toISOString().split('T')[0]
}

/**
 * Resolve a Timeframe filter value to the `from` bound the Cases and
 * Verifications APIs expect (YYYY-MM-DD). Unknown values fall back to the past
 * day, matching the behaviour these pages have always had.
 */
export const resolveTimeframeFrom = (timeframe: string): string => {
  switch (timeframe) {
    case PAST_24_HOURS:
      return formatYYYYMMDD(getDayBefore())
    case PAST_7_DAYS:
      return formatYYYYMMDD(getWeekBefore())
    case PAST_30_DAYS:
      return formatYYYYMMDD(get30DaysBefore())
    case PAST_YEAR:
      return formatYYYYMMDD(getOneYearBefore())
    case YEAR_TO_DATE:
      return formatYYYYMMDD(getYearToDate())
    default:
      return formatYYYYMMDD(getDayBefore())
  }
}

/**
 * Format date as relative time (e.g., "3 Days Ago", "1 Hour Ago")
 * @example 3 Days Ago (en-US)
 * @example منذ ٣ أيام (ar)
 */
export const formatRelativeTime = (datetime: string | Date, locale?: string): string => {
  const date = new Date(datetime)
  const now = new Date()
  const targetLocale = locale || getCurrentLocale()

  const diffMs = now.getTime() - date.getTime()
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)
  const diffWeeks = Math.floor(diffDays / 7)
  const diffMonths = Math.floor(diffDays / 30)
  const diffYears = Math.floor(diffDays / 365)

  const rtf = new Intl.RelativeTimeFormat(targetLocale, { numeric: 'auto' })

  if (diffYears > 0) {
    return rtf.format(-diffYears, 'year')
  }
  if (diffMonths > 0) {
    return rtf.format(-diffMonths, 'month')
  }
  if (diffWeeks > 0) {
    return rtf.format(-diffWeeks, 'week')
  }
  if (diffDays > 0) {
    return rtf.format(-diffDays, 'day')
  }
  if (diffHours > 0) {
    return rtf.format(-diffHours, 'hour')
  }
  if (diffMinutes > 0) {
    return rtf.format(-diffMinutes, 'minute')
  }
  return rtf.format(-diffSeconds, 'second')
}
